import React, { useRef, useState } from 'react';
import { Modal, ScrollView, TouchableWithoutFeedback, View } from 'react-native';
import styled from '@emotion/native';
import { ComboboxItem } from './comboboxItem';
import ComboboxList from './comboboxList';
import { ODSText } from '../ODSText';
import { ODSIcon } from '../ODSIcon';
import { useTheme } from '@emotion/react';

const LabelText = styled.Text`
  color: ${(props) => props.theme.colors.text.primary};
  font-size: 14px;
`;

interface ComboboxProps {
  data: any[];
  renderItem: ({
    item,
    index,
    handleAction,
  }: {
    item: any;
    index: number;
    handleAction: (value: any) => void;
  }) => JSX.Element; // Render function for items
  leftIcon?: string;
  renderLeftIcon?: React.ReactNode;
  renderRightIcon?: React.ReactNode;
  rightIcon?: string;
  width?: number;
  height?: number;
  placeholder?: string;
  value?: any;
  disabled?: boolean;
  renderLabel?: React.ReactNode;
  label?: string;
  error?: boolean;
  onChange?: any;
  hints?: {
    hint: string;
    state: 'success' | 'error' | 'disabled' | 'default';
  }[];
}

export function ODSCombobox({
  data,
  renderItem,
  leftIcon,
  renderLeftIcon,
  rightIcon = 'TB-Caret-Down',
  hints,
  renderRightIcon,
  width,
  height,
  placeholder,
  value,
  disabled = false,
  renderLabel,
  label,
  error = false,
  onChange = {},
  ...props
}) {
  const [visible, setVisible] = useState(false);
  const theme = useTheme();
  const [selectedValue, setComboboxedValue] = useState(value);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const pressableRef = useRef(null);



  const handleAction = (actionValue: any) => {
    setComboboxedValue(actionValue);
    setVisible(false);
    onChange(actionValue);
  };
  const measurePressable = () => {
    if (pressableRef.current) {
      pressableRef.current.measure((x, y, width, height, pageX, pageY) => {
        setDropdownPosition({
          top: pageY + height,
          left: pageX,
          width: width
        });
      });
    }
  };
  const toggleDropdown = () => {
    if (!disabled) {
      if (!visible) {
        measurePressable();
      }
      setVisible(!visible);
    }
  };

  const getHintTextColor = (state: 'disabled' | 'error' | 'success' | 'default') => {
    switch (state) {
      case 'disabled':
        return theme.colors.disabled.text;
      case 'error':
        return theme.colors.feedback.error[700];
      case 'success':
        return theme.colors.feedback.success[700];
      default:
        return theme.colors.text.primary;
    }
  };
  const renderText = selectedValue ? selectedValue.label : placeholder;
  return (
    <View style={{ flex: 1, marginBottom: 10, position: 'relative' }}>
      {label ? (
        <ODSText
          type="sm-bold"
          color={disabled ? theme.colors.disabled.text : theme.colors.text.primary}
          paddingVertical={8}>
          {label}
        </ODSText>
      ) : (
        <View style={{ paddingVertical: 4 }}>{renderLabel}</View> || null
      )}
      <StyledPressable
        width={width}
        height={height}
        ref={pressableRef}
        onPress={toggleDropdown}
        error={error}
        disabled={disabled}
        isFocused={visible}
        {...props}>
        {leftIcon && (
          <StyledLeftIconContainer>
            <ODSIcon size={22} icon={leftIcon} />
          </StyledLeftIconContainer>
        )}
        {renderLeftIcon && <StyledLeftIconContainer>{renderLeftIcon}</StyledLeftIconContainer>}
        <ODSText
          flex={1}
          paddingHorizontal={12}
          type="md"
          numberOfLines={1}
          color={selectedValue ? theme.colors.text.primary : theme.colors.disabled.text}>
          {renderText}
        </ODSText>
        {rightIcon && (
          <StyledRightIconContainer>
            <ODSIcon size={22} icon={visible ? 'TB-Caret-Up' : rightIcon} />
          </StyledRightIconContainer>
        )}
        {renderRightIcon && <StyledRightIconContainer>{renderRightIcon}</StyledRightIconContainer>}
      </StyledPressable>
      {hints !== null &&
        hints !== undefined &&
        hints.map((item, index) => (
          <ODSText paddingVertical={10} type="xs" color={getHintTextColor(item.state)}>
            {item.hint}
          </ODSText>
        ))}
      <Modal
        visible={visible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setVisible(false)}>
        <TouchableWithoutFeedback onPress={() => setVisible(false)}>
          <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.1)' }}>
            <View
              style={{
                position: 'absolute',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                maxHeight: 172,
                backgroundColor: theme.colors.elements.invert,
                borderRadius: 4,
                borderWidth: 1,
                borderColor: theme.colors.other['border-neutral'],
                elevation: 5,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.25,
                shadowRadius: 3.84,
              }}>
              <ScrollView nestedScrollEnabled={true} keyboardShouldPersistTaps="handled">
                {data.map((item, index) => {
                  if (renderItem) {
                    return renderItem({ item, index, handleAction });
                  }
                  return (
                    <ComboboxItem
                      width={width}
                      height={height}
                      key={index}
                      value={item}
                      selectedValue={selectedValue || {}}
                      action={() => handleAction(item)}>
                      {item.icon && <ODSIcon icon={item.icon} size={20} />}
                      <ODSText numberOfLines={1} color={theme.colors.text.primary} type="md">
                        {item.label}
                      </ODSText>
                    </ComboboxItem>
                  );
                })}
              </ScrollView>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
}

const StyledPressable = styled.TouchableOpacity<{
  width?: number;
  height?: number;
  error: boolean;
  disabled: boolean;
}>`
  border-radius: 4px;
  flex-direction: row;
  align-items: center;
  position: relative;
  padding: 0 4px;
  border: ${(props) =>
    props.error
      ? `1px solid ${props.theme.colors.feedback.error[100]}`
      : props.isFocused
        ? `2px solid ${props.theme.colors.other['border-color']}`
        : `1px solid ${props.theme.colors.other['border-neutral']}`};

  ${({ width, height }) => {
    return `
        width: ${width ? width : '100%'};
        height: ${height ? height : 44}px;
      `;
  }};
  ${({ disabled, error, theme }) => `
  background-color: ${theme.colors.elements.invert};
  opacity: ${disabled ? 0.5 : 1};
  `}
`;

const StyledRightIconContainer = styled.View`
  margin-right: 8px;
`;
const StyledLeftIconContainer = styled.View`
  margin-left: 8px;
`;

export default ODSCombobox;
